import { PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { cssTransition } from 'react-toastify';
import { AnalyticsProvider } from '@steelbuy/analytics';
import { Authenticated, AuthProvider, RefreshToken, Unauthenticated } from '@steelbuy/auth';
import { useUserSelfDetails } from '@steelbuy/data-access';
import { ErrorHandler } from '@steelbuy/error';
import { RegionCode } from '@steelbuy/ts-shared';
import { LoadSelfUser } from '@steelbuy/ui-domain';
import { ToastContainerId, ToasterContainer } from '@steelbuy/ui-primitive';
import { createVendorConfig, VendorConfig, VendorProvider, regionToVendor } from '@steelbuy/vendor';

import { AppMainLayout } from './components/app-layout/app-main-layout/AppMainLayout';
import { AppLayout } from './components/app-layout/AppLayout';
import { LocationAwareGenericError } from './components/generic-error/LocationAwareGenericError';
import { LanguageSelection } from './components/language-selection/LanguageSelection';
import { Navigation } from './components/navigation/Navigation';
import { ScrollPositionContextProvider } from './components/scroll-position/ScrollPositionContext';
import { LoginRouter } from './router/LoginRouter';
import { Router } from './router/Router';
import { RoutePath } from './router/Routes';
import { UnauthenticatedRouter } from './router/UnauthenticatedRouter';
import { UNAUTHENTICATE_SIGNAL } from '../store/store';
import { appVendorConfig } from '../vendor/vendorConfig';

import 'react-toastify/dist/ReactToastify.css';

const modalTransition = cssTransition({
    enter: 'zoom-in',
    exit: 'noop',
    collapse: false,
});

const VendorProviders = ({ children }: PropsWithChildren) => {
    const { data } = useUserSelfDetails();
    const [vendorConfig, setVendorConfig] = useState<VendorConfig>(
        createVendorConfig(appVendorConfig).resolve(regionToVendor(data?.region.regionCode))
    );

    useEffect(() => {
        setVendorConfig(createVendorConfig(appVendorConfig).resolve(regionToVendor(data?.region.regionCode)));
    }, [data?.region.regionCode]);

    return (
        <VendorProvider vendor={regionToVendor(data?.region.regionCode)} config={vendorConfig}>
            {children}
        </VendorProvider>
    );
};

export const App = () => {
    const { t } = useTranslation('translation');
    const dispatch = useDispatch();
    const { data: userModel } = useUserSelfDetails();
    const navigate = useNavigate();

    useEffect(() => {
        document.title = t('application.pageTitle');
    }, [navigator.language]);

    const handleReauthenticate = () => {
        dispatch({ type: UNAUTHENTICATE_SIGNAL });
    };

    const handleUnauthenticate = async (refreshToken?: RefreshToken, queryParam?: string) => {
        dispatch({ type: UNAUTHENTICATE_SIGNAL });
        navigate(RoutePath.LOGOUT, {
            state: {
                queryParam,
                userId: userModel?.id,
                refreshToken,
            },
        });
    };

    const handleError = (error: Error, resetError: () => void) => (
        <LocationAwareGenericError error={error} resetError={resetError} />
    );

    return (
        <AuthProvider onReauthenticate={handleReauthenticate} onUnauthenticate={handleUnauthenticate}>
            <LoginRouter>
                <Authenticated>
                    <LoadSelfUser>
                        <LanguageSelection>
                            <VendorProviders>
                                <AnalyticsProvider>
                                    <AppLayout>
                                        <Navigation />
                                        <AppMainLayout isAuthenticated>
                                            <ErrorHandler errorComponent={handleError}>
                                                <ScrollPositionContextProvider>
                                                    <Router />
                                                </ScrollPositionContextProvider>
                                            </ErrorHandler>
                                        </AppMainLayout>
                                    </AppLayout>
                                </AnalyticsProvider>
                            </VendorProviders>
                        </LanguageSelection>
                    </LoadSelfUser>
                </Authenticated>
                <Unauthenticated>
                    <VendorProvider
                        vendor={regionToVendor(RegionCode.us)}
                        config={createVendorConfig(appVendorConfig).resolve(regionToVendor(RegionCode.us))}
                    >
                        <AppLayout>
                            <>
                                <Navigation isAuthenticated={false} />
                                <AppMainLayout isAuthenticated={false}>
                                    <ErrorHandler errorComponent={handleError}>
                                        <UnauthenticatedRouter />
                                    </ErrorHandler>
                                </AppMainLayout>
                            </>
                        </AppLayout>
                    </VendorProvider>
                </Unauthenticated>
            </LoginRouter>
            <ToasterContainer />
            <ToasterContainer containerId={ToastContainerId.MODAL} transition={modalTransition} />
            <ToasterContainer containerId={ToastContainerId.CRITICAL} />
        </AuthProvider>
    );
};
